// Playback: walking the year one section at a time.
//
// This module only decides WHEN the next step happens — stepping is showFrame's
// job, and everything that follows from the new date is a `dateIdx` subscriber.
//
// It also reports how far through the current step it is, every frame. That is
// still only a statement about time: what to do with it belongs to whoever is
// drawing (the ruler sweeps its marker with it), and this file has no opinion
// about the ruler's geometry.

import { $ } from '../dom.ts';
import { state } from '../state.ts';

export interface PlaybackDeps {
  showFrame: (idx: number) => void;
  /** 0–1 through the current step, every animation frame while playing. */
  onProgress: (p: number) => void;
}

let deps: PlaybackDeps;
let rafId: number | null = null;
let lastStep = 0;

function tick(ts: number) {
  if (!state.playing) return;
  const interval = 1000 / state.fps;
  // lastStep starts at 0, so the first tick after play() always steps: pressing
  // play moves immediately rather than after a silent three seconds.
  if (ts - lastStep >= interval) {
    lastStep = ts;
    deps.showFrame(state.dateIdx + 1);
  }
  deps.onProgress(Math.min(1, (ts - lastStep) / interval));
  rafId = requestAnimationFrame(tick);
}

export function play() {
  if (state.playing) return;
  state.playing = true;
  $('play').textContent = '❚❚';
  lastStep = 0;
  rafId = requestAnimationFrame(tick);
}

export function pause() {
  state.playing = false;
  $('play').textContent = '▶';
  if (rafId) cancelAnimationFrame(rafId);
  // Stopping mid-step leaves the marker between two dekads, which would read as
  // a scrub landing off-slot. Zero progress puts it back on the one showing.
  deps?.onProgress?.(0);
}

export function togglePlay() {
  state.playing ? pause() : play();
}

export function wirePlayback(d: PlaybackDeps) {
  deps = d;
  $('play').addEventListener('click', togglePlay);
}
